import type { MemberSearchData } from '../dto/EntityRepository';

export type LandingArchiveCandidate = {
  username: string;
  aui: string;
  handle: string;
  /** Archive Index의 Billboard 이미지가 있을 때만 제공됩니다. */
  originUrl?: string;
  /** `0` = 검색으로 앞에 붙은 행, `1` = 아카이브. 없으면 아카이브 정렬만 적용 */
  listSegment?: 0 | 1;
  /** 병합 목록 기준 순서(검색 구간 내 API 순 유지) */
  orderInMerged?: number;
};

export type LandingArchiveGroup = {
  letter: string;
  label: string;
  candidates: LandingArchiveCandidate[];
};

export const MAX_ARCHIVE_INDEX_ROWS_PER_COLUMN = 35;  // 18 -> 20 -> 22 -> 27
export const MAX_ARCHIVE_INDEX_ROWS_PER_COLUMN_MOBILE = 28;
export const LANDING_COLUMN_WIDTH = 'clamp(310px, 35vw, 360px)';
export const LANDING_COLUMN_GAP = 'clamp(14px, 2vw, 24px)';
const LANDING_COLUMN_WIDTH2_MIN_PX = 120;
const LANDING_COLUMN_WIDTH2_VW = 10;
const LANDING_COLUMN_WIDTH2_MAX_PX = 135;
const LANDING_COLUMN_GAP2_MIN_PX = 8;
const LANDING_COLUMN_GAP2_VW = 2;
const LANDING_COLUMN_GAP2_MAX_PX = 20;

export const LANDING_COLUMN_WIDTH2 =
  `clamp(${LANDING_COLUMN_WIDTH2_MIN_PX}px, ${LANDING_COLUMN_WIDTH2_VW}vw, ${LANDING_COLUMN_WIDTH2_MAX_PX}px)`;
export const LANDING_COLUMN_WIDTH2_MOBILE = 'clamp(120px, 30vw, 120px)';
export const LANDING_COLUMN_GAP2 =
  `clamp(${LANDING_COLUMN_GAP2_MIN_PX}px, ${LANDING_COLUMN_GAP2_VW}vw, ${LANDING_COLUMN_GAP2_MAX_PX}px)`;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const getLandingColumnWidth2Px = (viewportWidth: number): number =>
  clamp(
    viewportWidth * LANDING_COLUMN_WIDTH2_VW / 100,
    LANDING_COLUMN_WIDTH2_MIN_PX,
    LANDING_COLUMN_WIDTH2_MAX_PX,
  );

export const getLandingColumnGap2Px = (viewportWidth: number): number =>
  clamp(
    viewportWidth * LANDING_COLUMN_GAP2_VW / 100,
    LANDING_COLUMN_GAP2_MIN_PX,
    LANDING_COLUMN_GAP2_MAX_PX,
  );

/** LandingArchiveIndex `ColumnList` 전체 너비 */
export const getLandingArchiveIndex2ContentWidth = (
  columnCount: number,
  isMobile: boolean
): string => {
  const columnWidth = isMobile ? LANDING_COLUMN_WIDTH2_MOBILE : LANDING_COLUMN_WIDTH2;
  if (columnCount <= 1) return columnWidth;
  return `calc((${columnWidth} * ${columnCount}) + (${LANDING_COLUMN_GAP2} * ${columnCount - 1}))`;
};

/** Welcome 행·Archive `ColumnList`·`Column` 폭/간격 전환 (검색·열 분할 시) */
export const LANDING_ARCHIVE_LAYOUT_TRANSITION =
  'width 0.48s cubic-bezier(0.25, 0.8, 0.25, 1), gap 0.48s cubic-bezier(0.25, 0.8, 0.25, 1), min-width 0.48s cubic-bezier(0.25, 0.8, 0.25, 1), max-width 0.48s cubic-bezier(0.25, 0.8, 0.25, 1)';

const HANGUL_SYLLABLE_START = 0xac00;
const HANGUL_SYLLABLE_END = 0xd7a3;

/** 한글 음절 → 초성 자모(19) */
const CHOSEONG_BY_INDEX = [
  'ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
] as const;

/** 쌍자음·된소리를 기본 자음 그룹으로 묶음 (ㄲ→ㄱ, ㄸ→ㄷ …) */
const CHOSEONG_GROUP_KEY: Record<string, string> = {
  'ㄱ': 'ㄱ',
  'ㄲ': 'ㄱ',
  'ㄴ': 'ㄴ',
  'ㄷ': 'ㄷ',
  'ㄸ': 'ㄷ',
  'ㄹ': 'ㄹ',
  'ㅁ': 'ㅁ',
  'ㅂ': 'ㅂ',
  'ㅃ': 'ㅂ',
  'ㅅ': 'ㅅ',
  'ㅆ': 'ㅅ',
  'ㅇ': 'ㅇ',
  'ㅈ': 'ㅈ',
  'ㅉ': 'ㅈ',
  'ㅊ': 'ㅊ',
  'ㅋ': 'ㅋ',
  'ㅌ': 'ㅌ',
  'ㅍ': 'ㅍ',
  'ㅎ': 'ㅎ',
};

const LATIN_GROUP_ORDER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const KOREAN_GROUP_ORDER = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'] as const;

const getMergedChoseongFromCodePoint = (codePoint: number): string | null => {
  if (codePoint < HANGUL_SYLLABLE_START || codePoint > HANGUL_SYLLABLE_END) return null;
  const choseongIndex = Math.floor((codePoint - HANGUL_SYLLABLE_START) / 588);
  const jamo = CHOSEONG_BY_INDEX[choseongIndex];
  return jamo ? CHOSEONG_GROUP_KEY[jamo] ?? null : null;
};

/** 그룹 키: A–Z | ㄱ·ㄴ·ㄷ… | '#' */
const getGroupKey = (username: string): string => {
  const trimmed = username.trim();
  if (!trimmed) return '#';

  const firstUnit = trimmed.charAt(0);
  if (/^[a-zA-Z]$/.test(firstUnit)) {
    return firstUnit.toUpperCase();
  }

  const cp = trimmed.codePointAt(0);
  if (cp === undefined) return '#';

  const hangulKey = getMergedChoseongFromCodePoint(cp);
  if (hangulKey) return hangulKey;

  return '#';
};

const getGroupSortIndex = (key: string): number => {
  const latinIdx = LATIN_GROUP_ORDER.indexOf(key);
  if (latinIdx !== -1) return latinIdx;

  const koIdx = (KOREAN_GROUP_ORDER as readonly string[]).indexOf(key);
  if (koIdx !== -1) return 100 + koIdx;

  return 200;
};

const getGroupLabel = (key: string): string => {
  if (key === '#') return '##';
  if (/^[A-Z]$/.test(key)) return `${key}${key.toLowerCase()}`;
  return key;
};

/** 검색 구간 전용 그룹 키(표시 라벨은 `searchResultsGroupLabel`로 별도 지정). UI에서 검색 블록 식별용 */
export const LANDING_ARCHIVE_SEARCH_GROUP_LETTER = '__landing_search_results__';

/** View Transitions API: 그룹 `letter`당 페이지 내 유일한 이름(열 간 이동 보간) */
export function landingArchiveGroupViewTransitionName(letter: string): string {
  if (letter === LANDING_ARCHIVE_SEARCH_GROUP_LETTER) return 'landing-vt-search';
  if (letter === '#') return 'landing-vt-hash';
  return `landing-vt-${encodeURIComponent(letter).replace(/%/g, '_')}`;
}

const groupLetterForCandidate = (c: LandingArchiveCandidate): string => {
  if (c.listSegment === 0) return LANDING_ARCHIVE_SEARCH_GROUP_LETTER;
  return getGroupKey(c.username);
};

const labelForGroupLetter = (letter: string, searchResultsGroupLabel?: string): string => {
  if (letter === LANDING_ARCHIVE_SEARCH_GROUP_LETTER) {
    return searchResultsGroupLabel ?? '';
  }
  return getGroupLabel(letter);
};

const buildGroupedCandidates = (
  candidates: LandingArchiveCandidate[],
  searchResultsGroupLabel?: string
): LandingArchiveGroup[] => {
  const sorted = [...candidates].sort((a, b) => {
    const segA = a.listSegment ?? 1;
    const segB = b.listSegment ?? 1;
    if (segA !== segB) return segA - segB;

    if (segA === 0) {
      return (a.orderInMerged ?? 0) - (b.orderInMerged ?? 0);
    }

    const ka = getGroupKey(a.username);
    const kb = getGroupKey(b.username);
    const ia = getGroupSortIndex(ka);
    const ib = getGroupSortIndex(kb);
    if (ia !== ib) return ia - ib;
    return a.username.localeCompare(b.username, 'ko', { sensitivity: 'base' });
  });

  return sorted.reduce<LandingArchiveGroup[]>((groups, candidate) => {
    const letter = groupLetterForCandidate(candidate);
    const existingGroup = groups[groups.length - 1];

    if (existingGroup && existingGroup.letter === letter) {
      existingGroup.candidates.push(candidate);
      return groups;
    }

    groups.push({
      letter,
      label: labelForGroupLetter(letter, searchResultsGroupLabel),
      candidates: [candidate],
    });

    return groups;
  }, []);
};

/** 라벨이 있으면 헤더 1행 포함. 빈 라벨(Landing 검색 등)은 candidate 행만 집계 */
const countGroupRows = (group: LandingArchiveGroup): number =>
  group.candidates.length + (group.label ? 1 : 0);

const splitGroupsIntoColumns = (
  groups: LandingArchiveGroup[],
  maxRowsPerColumn: number,
  reservedRowsInFirstColumn = 0
): LandingArchiveGroup[][] => {
  return groups.reduce<LandingArchiveGroup[][]>((columns, group) => {
    const groupRowCount = countGroupRows(group);
    const currentColumn = columns[columns.length - 1];
    const reserved = columns.length === 1 ? reservedRowsInFirstColumn : 0;
    const currentRowCount =
      reserved +
      currentColumn.reduce((total, currentGroup) => total + countGroupRows(currentGroup), 0);

    if (currentColumn.length === 0 || currentRowCount + groupRowCount <= maxRowsPerColumn) {
      currentColumn.push(group);
      return columns;
    }

    columns.push([group]);
    return columns;
  }, [[]]);
};

export type BuildArchiveIndexColumnsOptions = {
  /** `selectLandingMemberDisplayList`에서 검색 행 개수(앞쪽). 있으면 해당 구간은 검색 순서 우선, 나머지는 아카이브 그룹 정렬 */
  leadingSearchCount?: number;
  /** 검색 구간 그룹 헤더. 빈 문자열이면 UI에서 라벨 생략 */
  searchResultsGroupLabel?: string;
  /** 열당 최대 행 수. 기본 `MAX_ARCHIVE_INDEX_ROWS_PER_COLUMN` */
  maxRowsPerColumn?: number;
  /** 첫 열에서 미리 쓰는 행 수 */
  reservedRowsInFirstColumn?: number;
};

const toArchiveCandidates = (
  members: MemberSearchData[],
  leadingSearchCount = 0
): LandingArchiveCandidate[] => {
  const leading = Math.min(Math.max(0, leadingSearchCount), members.length);

  return members.map((m, index) => {
    const base: LandingArchiveCandidate = {
      username: m.username,
      aui: m.aui,
      handle: m.handle,
      originUrl: m.originUrl,
    };
    if (leading > 0) {
      base.listSegment = index < leading ? 0 : 1;
      base.orderInMerged = index;
    }
    return base;
  });
};

/** Archive Index 표시용: 멤버 목록 → 알파벳/초성 그룹 (열 분할 없음, CSS columns용) */
export const buildArchiveIndexGroups = (
  members: MemberSearchData[],
  options?: Pick<BuildArchiveIndexColumnsOptions, 'leadingSearchCount' | 'searchResultsGroupLabel'>
): LandingArchiveGroup[] => {
  if (members.length === 0) return [];

  return buildGroupedCandidates(
    toArchiveCandidates(members, options?.leadingSearchCount ?? 0),
    options?.searchResultsGroupLabel
  );
};

/** Archive Index 표시용: 멤버 목록 → 행 수 기준 다열 그룹 구조 */
export const buildArchiveIndexColumns = (
  members: MemberSearchData[],
  options?: BuildArchiveIndexColumnsOptions
): LandingArchiveGroup[][] => {
  if (members.length === 0) return [];

  return splitGroupsIntoColumns(
    buildArchiveIndexGroups(members, options),
    options?.maxRowsPerColumn ?? MAX_ARCHIVE_INDEX_ROWS_PER_COLUMN,
    options?.reservedRowsInFirstColumn ?? 0,
  );
};
